/**
 * usePageTracking — fires a `page_view` analytics event on every route change.
 *
 * Derives a readable page name from the pathname, e.g.:
 *   "/"              → "home"
 *   "/configs/abc"   → "config_detail"
 *   "/generate/jobs" → "generation_jobs"
 *
 * Import from "../analytics" (the public API) rather than directly from this file.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { mpTrack } from "./mixpanel";
import { EVENTS } from "./events";
import { recordPage } from "../utils/bugCapture";

function pathToPageName(pathname: string): string {
  const clean = pathname.replace(/^\//, "").replace(/\/+$/, "") || "home";
  // Map well-known paths to human-readable names.
  const mapping: Record<string, string> = {
    "": "home",
    "welcome": "onboarding",
    "new": "create_deliverable",
    "setup": "setup_migrate",
    "formats": "formats",
    "templates": "templates",
    "configs": "config_list",
    "merge": "merge",
    "generate": "generate",
    "batch": "batch",
    "generate/jobs": "generation_jobs",
    "getting-started": "getting_started",
    "specs": "spec_registry",
    "admin": "admin",
    "admin/ai-usage": "admin_ai_usage",
    "analytics": "analytics",
    "org": "org_admin",
    "rejections": "rejections",
    "marketplace": "marketplace",
    "profile": "profile",
    "login": "login",
    "register": "register",
  };

  if (clean in mapping) return mapping[clean];

  // Dynamic segments: configs/:id, templates/:id, etc.
  if (/^configs\/[^/]+\/history$/.test(clean)) return "config_history";
  if (/^configs\/[^/]+$/.test(clean)) return "config_detail";
  if (/^templates\/[^/]+$/.test(clean)) return "template_detail";
  if (/^generate\/jobs\/[^/]+$/.test(clean)) return "generation_job_detail";

  // Fallback: replace slashes and dashes with underscores.
  return clean.replace(/\//g, "_").replace(/-/g, "_");
}

export function usePageTracking(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    const page = pathToPageName(pathname);
    // Ship ONLY the normalized page name — never the raw pathname, which can carry config/template/job
    // UUIDs (PII-ish identifiers) off to Mixpanel.
    mpTrack(EVENTS.page_view, { page });
    // The bug-report buffer keeps the raw path locally (only ever sent in a report the user files).
    recordPage(pathname);
  }, [pathname]);
}
